// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import './styles/main.css';

import type { Finding, GbnfResult, Status, TargetId } from './types';
import type { RunResult } from './pipeline';
import { EventLog } from './eventlog';
import { initGlossary } from './glossary';
import { createDrawer, createModalHost, el, formatCount, toast } from './ui/shell';
import { LIMITS, MATRIX_KEY, PRIVACY_NOT, PRIVACY_PROTECTED, PRIVACY_TRUST } from './copy';
import { TARGETS } from './targets';
import { buildArtefacts, bundle, type Artefact } from './emit/artefacts';
import { compactDiff, lineDiff } from './ingest/parse';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
};

const DEFAULT_TARGETS: TargetId[] = [
  'openai-strict',
  'anthropic-strict',
  'gemini-parameters',
  'gbnf-llamacpp',
];

const STATUS_CLASS: Readonly<Record<Status, string>> = {
  REQ: 'st-req',
  SUP: 'st-sup',
  ACC: 'st-acc',
  REJECT: 'st-rej',
  DROP: 'st-drop',
  UNKNOWN: 'st-unk',
};

const log = new EventLog($<HTMLOListElement>('drawer-list'));
let worker: Worker | null = null;
let analysis: RunResult | null = null;
let artefacts: Artefact[] = [];
let selected = new Set<TargetId>(DEFAULT_TARGETS);

// ── theme ──────────────────────────────────────────────────────────────────

function applyTheme(theme: 'dark' | 'light'): void {
  document.documentElement.dataset['theme'] = theme;
  try {
    localStorage.setItem('toolwright:theme', theme);
  } catch {
    /* private mode — the tool works without a stored preference */
  }
}

// ── worker ─────────────────────────────────────────────────────────────────

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (e: MessageEvent) => {
    const msg = e.data as
      | { kind: 'log'; level: 'info' | 'good' | 'warn' | 'error'; message: string }
      | { kind: 'progress'; done: number; total: number; label: string }
      | { kind: 'done'; analysis: RunResult }
      | { kind: 'error'; message: string };

    if (msg.kind === 'log') log.emit(msg.level, msg.message);
    else if (msg.kind === 'progress') setProgress(msg.done, msg.total, msg.label);
    else if (msg.kind === 'done') render(msg.analysis);
    else showError(msg.message);
  };
  worker.onerror = (e) => showError(e.message || 'The worker failed to start.');
  return worker;
}

function setProgress(done: number, total: number, label: string): void {
  const box = $('progress');
  if (done >= total) {
    box.setAttribute('hidden', '');
    return;
  }
  box.removeAttribute('hidden');
  $('progress-fill').style.width = `${Math.round((done / total) * 100)}%`;
  $('progress-label').textContent = label;
}

function showError(message: string): void {
  setProgress(1, 1, '');
  const box = $('error');
  $('error-text').textContent = message;
  box.removeAttribute('hidden');
  log.emit('error', message);
}

function clearError(): void {
  $('error').setAttribute('hidden', '');
}

// ── ingest ─────────────────────────────────────────────────────────────────

function analyse(text: string): void {
  clearError();
  if (!text.trim()) {
    $('input-note').textContent = 'Paste a tool belt, or try the sample.';
    return;
  }
  $('start-over').removeAttribute('hidden');
  log.emit('info', `Analysing ${formatCount(text.length)} characters.`);
  ensureWorker().postMessage({ kind: 'analyse', text, targets: [...selected] });
}

function retarget(): void {
  if (!analysis) return;
  ensureWorker().postMessage({ kind: 'retarget', targets: [...selected] });
}

/**
 * One ingestion path for every source — paste, drop, file picker and sample all land here, so the
 * sample exercises exactly the code a real belt does. A sample with its own code path proves nothing.
 */
function ingestText(text: string, source: string): void {
  $<HTMLTextAreaElement>('paste-input').value = text;
  log.emit('info', `Read a belt from ${source}.`);
  analyse(text);
}

async function loadSample(path: string, label: string): Promise<void> {
  try {
    const res = await fetch(path);
    if (!res.ok) throw new Error(`${res.status}`);
    ingestText(await res.text(), label);
  } catch (e) {
    showError(`Could not load the sample: ${e instanceof Error ? e.message : String(e)}`);
  }
}

function readFile(file: File): void {
  const reader = new FileReader();
  reader.onload = () => ingestText(String(reader.result ?? ''), file.name);
  reader.onerror = () => showError(`Could not read ${file.name}.`);
  reader.readAsText(file);
}

// ── render ─────────────────────────────────────────────────────────────────

function render(result: RunResult): void {
  analysis = result;
  setProgress(1, 1, '');
  clearError();

  renderRepair(result);
  renderMatrix(result);
  renderFindings(result);
  renderGbnf(result);
  renderExports(result);

  $('input-note').textContent =
    `${result.belt.tools.length} tool${result.belt.tools.length === 1 ? '' : 's'} · ` +
    `${result.belt.dialect} · analysed in ${result.elapsedMs} ms, entirely in this tab.`;
}

function renderRepair(result: RunResult): void {
  const panel = $('repair-panel');
  if (!result.repaired.changed) {
    panel.setAttribute('hidden', '');
    return;
  }
  panel.removeAttribute('hidden');
  $('repair-note').textContent =
    'Your paste was not valid JSON. This is what Toolwright parsed instead — check it before trusting anything below.';
  const pre = $('repair-diff');
  pre.replaceChildren();
  for (const op of compactDiff(lineDiff(result.repaired.original, result.repaired.repaired))) {
    const line = el('span', { class: `d${op.kind === '+' ? 'ins' : op.kind === '-' ? 'del' : 'same'}` }, [
      `${op.kind} ${op.text}\n`,
    ]);
    pre.append(line);
  }
}

function renderTargetPicker(): void {
  const box = $('target-picker');
  box.replaceChildren();
  for (const t of TARGETS) {
    const id = `t-${t.id}`;
    const label = el('label', { class: 'target-chip', title: t.blurb });
    const input = el('input', { type: 'checkbox', id }) as HTMLInputElement;
    input.checked = selected.has(t.id);
    input.addEventListener('change', () => {
      if (input.checked) selected.add(t.id);
      else selected.delete(t.id);
      try {
        localStorage.setItem('toolwright:targets', JSON.stringify([...selected]));
      } catch {
        /* ignore */
      }
      retarget();
    });
    label.append(input, el('span', {}, [t.short]));
    box.append(label);
  }
}

/** The worst status wins — a cell is as bad as its worst finding. */
const SEVERITY_ORDER: Status[] = ['REJECT', 'REQ', 'DROP', 'UNKNOWN', 'ACC', 'SUP'];

function renderMatrix(result: RunResult): void {
  const panel = $('matrix-panel');
  panel.removeAttribute('hidden');

  const targets = TARGETS.filter((t) => selected.has(t.id));
  const head = $('matrix-head');
  head.replaceChildren(el('th', { class: 'tool-col' }, ['tool']));
  for (const t of targets) {
    const th = el('th', { class: 'n', title: t.blurb }, [t.short]);
    head.append(th);
  }

  const body = $('matrix-body');
  body.replaceChildren();

  for (const tool of result.belt.tools) {
    const tr = el('tr');
    const nameCell = el('td', { class: 'tool-col' });
    nameCell.append(el('code', {}, [tool.name]));
    if (tool.namespace) nameCell.append(el('span', { class: 'ns' }, [` in ${tool.namespace}`]));
    if (tool.kind === 'custom') nameCell.append(el('span', { class: 'ns' }, [' · custom tool, no schema']));
    tr.append(nameCell);

    for (const t of targets) {
      const mine = result.findings.filter(
        (f) => f.tool === tool.name && (f.target === t.id || f.target === 'all'),
      );
      const worst = SEVERITY_ORDER.find((s) => mine.some((f) => f.status === s));
      const errs = mine.filter((f) => f.severity === 'error').length;
      const warns = mine.filter((f) => f.severity === 'warn').length;

      const td = el('td', { class: 'n' });
      const btn = el('button', {
        type: 'button',
        class: `cell ${worst ? STATUS_CLASS[worst] : 'st-ok'}`,
      }) as HTMLButtonElement;
      btn.textContent = errs > 0 ? String(errs) : warns > 0 ? String(warns) : '·';
      btn.title =
        errs > 0
          ? `${errs} blocking finding${errs === 1 ? '' : 's'} on ${t.label}`
          : warns > 0
            ? `${warns} warning${warns === 1 ? '' : 's'} on ${t.label}`
            : `Nothing found on ${t.label}`;
      btn.addEventListener('click', () => {
        ($<HTMLSelectElement>('filter-target')).value = t.id;
        ($<HTMLSelectElement>('filter-severity')).value = 'all';
        renderFindings(result, tool.name);
        $('findings-panel').scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      td.append(btn);
      tr.append(td);
    }
    body.append(tr);
  }

  const errors = result.findings.filter((f) => f.severity === 'error');
  const blockedTargets = new Set(errors.map((f) => f.target).filter((t) => t !== 'all'));
  $('matrix-summary').textContent =
    errors.length === 0
      ? 'Nothing blocking found on the selected targets.'
      : `${errors.length} blocking finding${errors.length === 1 ? '' : 's'} across ${blockedTargets.size || 'all'} target${blockedTargets.size === 1 ? '' : 's'}. One bad tool fails the whole array.`;

  const key = $('matrix-key');
  key.replaceChildren();
  for (const [cls, text] of MATRIX_KEY) {
    key.append(el('span', { class: `key-swatch st-${cls}` }), el('span', { class: 'key-text' }, [`${text}  `]));
  }
}

function renderFindings(result: RunResult, onlyTool?: string): void {
  $('findings-panel').removeAttribute('hidden');

  const targetSel = $<HTMLSelectElement>('filter-target');
  if (targetSel.options.length <= 1) {
    for (const t of TARGETS) {
      const o = document.createElement('option');
      o.value = t.id;
      o.textContent = t.label;
      targetSel.append(o);
    }
    const o = document.createElement('option');
    o.value = 'all';
    o.textContent = 'Wrong everywhere';
    targetSel.append(o);
  }

  const sev = $<HTMLSelectElement>('filter-severity').value;
  const tgt = targetSel.value;

  const list = result.findings
    .filter((f) => (sev === 'error' ? f.severity === 'error' : sev === 'all' ? f.severity !== 'info' : true))
    .filter((f) => (tgt ? f.target === tgt : true))
    .filter((f) => (onlyTool ? f.tool === onlyTool : true))
    .sort((a, b) => rank(a) - rank(b));

  $('finding-count').textContent = `${formatCount(list.length)} shown of ${formatCount(result.findings.length)}`;

  const ul = $('findings-list');
  ul.replaceChildren();

  if (list.length === 0) {
    ul.append(el('li', { class: 'finding' }, ['Nothing matches this filter.']));
    return;
  }

  for (const f of list.slice(0, 500)) {
    const li = el('li', { class: `finding sev-${f.severity}` });
    const head = el('div', { class: 'finding-head' });
    head.append(
      el('span', { class: `chip ${STATUS_CLASS[f.status]}` }, [f.status.toLowerCase()]),
      el('code', { class: 'finding-tool' }, [f.tool]),
      el('span', { class: 'finding-ptr' }, [f.pointer || '(root)']),
      el('span', { class: 'chip prov', title: 'Where this rule’s authority comes from' }, [f.provenance]),
      el('span', { class: 'finding-target' }, [labelFor(f.target)]),
    );
    li.append(head, el('p', { class: 'finding-msg' }, [f.message]));
    if (f.providerError) {
      li.append(el('pre', { class: 'provider-error' }, [f.providerError]));
    }
    if (f.fix) li.append(el('p', { class: 'finding-fix' }, [`Fix: ${f.fix}`]));
    ul.append(li);
  }
  if (list.length > 500) {
    ul.append(el('li', { class: 'finding' }, [`… and ${formatCount(list.length - 500)} more, in the CSV.`]));
  }
}

function rank(f: Finding): number {
  return f.severity === 'error' ? 0 : f.severity === 'warn' ? 1 : 2;
}

function labelFor(id: Finding['target']): string {
  if (id === 'all') return 'every target';
  return TARGETS.find((t) => t.id === id)?.label ?? id;
}

function renderGbnf(result: RunResult): void {
  const panel = $('gbnf-panel');
  const llama = result.beltGbnf.llamacpp;
  if (!llama) {
    panel.setAttribute('hidden', '');
    return;
  }
  panel.removeAttribute('hidden');

  const drops = Object.values(result.gbnf).reduce((a: number, g: GbnfResult) => a + g.drops.length, 0);
  $('gbnf-note').textContent =
    llama.errors.length > 0
      ? `The grammar could not be built: ${llama.errors.length} blocking error${llama.errors.length === 1 ? '' : 's'}. llama.cpp reports all of them at once, and so does this.`
      : `Compiled. ${drops} constraint${drops === 1 ? '' : 's'} in your schemas did not survive into the grammar.`;

  const orderBox = $('keyorder');
  orderBox.replaceChildren();
  const orders = Object.entries(llama.keyOrder).slice(0, 12);
  if (orders.length === 0) {
    orderBox.append(el('p', { class: 'field-note' }, ['No object rules in this grammar.']));
  }
  for (const [rule, keys] of orders) {
    const row = el('div', { class: 'order-row' });
    row.append(el('code', {}, [rule]), el('span', { class: 'order-keys' }, [keys.join(' → ')]));
    orderBox.append(row);
  }

  const dropBox = $('gbnf-drops');
  dropBox.replaceChildren();
  const flat = Object.entries(result.gbnf).flatMap(([tool, g]) => g.drops.map((d) => ({ tool, ...d })));
  if (flat.length === 0) {
    dropBox.append(el('p', { class: 'field-note' }, ['Nothing was dropped.']));
  }
  for (const d of flat.slice(0, 40)) {
    const row = el('div', { class: 'order-row' });
    row.append(
      el('code', {}, [`${d.tool}${d.pointer || ''}`]),
      el('span', { class: 'order-keys' }, [`${d.keyword} — ${d.reason}`]),
    );
    dropBox.append(row);
  }
  if (flat.length > 40) {
    dropBox.append(el('p', { class: 'field-note' }, [`… and ${formatCount(flat.length - 40)} more.`]));
  }

  const pre = $('grammar-text');
  pre.textContent =
    llama.errors.length > 0 ? llama.errors.map((e) => `error: ${e}`).join('\n') : llama.grammar;

  if (llama.warnings.length > 0) {
    log.emit('warn', `Grammar warnings (llama.cpp prints these to stderr only): ${llama.warnings.join('; ')}`);
  }
}

function renderExports(result: RunResult): void {
  artefacts = buildArtefacts(result.belt, result.transpiles, result.gbnf, result.beltGbnf, result.findings);
  const panel = $('export-panel');
  panel.removeAttribute('hidden');

  const box = $('export-actions');
  box.replaceChildren();
  for (const a of artefacts) {
    const wrap = el('div', { class: 'export-item' });
    const btn = el('button', { type: 'button', class: 'btn' }, [a.filename]) as HTMLButtonElement;
    btn.addEventListener('click', () => download(a));
    wrap.append(btn, el('span', { class: 'field-note' }, [a.note]));
    box.append(wrap);
  }

  const share = $<HTMLButtonElement>('share');
  if (typeof navigator.share === 'function' && typeof navigator.canShare === 'function') {
    share.removeAttribute('hidden');
  }
}

function download(a: Artefact): void {
  const blob = new Blob([a.content], { type: `${a.mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = a.filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  log.emit('good', `Saved ${a.filename}.`);
}

// ── modals ─────────────────────────────────────────────────────────────────

const modalHost = createModalHost($('modal-host'));

function fillList(id: string, items: readonly string[]): void {
  const ul = document.getElementById(id);
  if (!ul) return;
  ul.replaceChildren();
  for (const item of items) ul.append(el('li', {}, [item]));
}

function openModal(which: string): void {
  const tpl = document.getElementById(`tpl-${which}`) as HTMLTemplateElement | null;
  if (!tpl) return;
  const body = tpl.content.cloneNode(true) as DocumentFragment;
  const titles: Record<string, string> = {
    how: 'How it works',
    privacy: 'Privacy & threat model',
    limits: 'What this cannot tell you',
    about: 'About Toolwright',
    keys: 'Keyboard shortcuts',
  };
  modalHost.show(titles[which] ?? which, body);
  if (which === 'privacy') {
    fillList('privacy-protected', PRIVACY_PROTECTED);
    fillList('privacy-not', PRIVACY_NOT);
    fillList('privacy-trust', PRIVACY_TRUST);
  }
  if (which === 'limits') fillList('limits-modal-list', LIMITS);
}

// ── boot ───────────────────────────────────────────────────────────────────

function boot(): void {
  let storedTheme: string | null = null;
  let storedTargets: string | null = null;
  try {
    storedTheme = localStorage.getItem('toolwright:theme');
    storedTargets = localStorage.getItem('toolwright:targets');
  } catch {
    /* ignore */
  }
  applyTheme(storedTheme === 'light' ? 'light' : 'dark');
  if (storedTargets) {
    try {
      const parsed = JSON.parse(storedTargets) as TargetId[];
      const valid = parsed.filter((t) => TARGETS.some((d) => d.id === t));
      if (valid.length > 0) selected = new Set(valid);
    } catch {
      /* ignore a corrupt preference */
    }
  }

  fillList('limits-list', LIMITS.slice(0, 4));
  renderTargetPicker();
  initGlossary($('tooltip'));

  const drawer = createDrawer(
    $('drawer'),
    $<HTMLButtonElement>('log-toggle'),
    $<HTMLButtonElement>('log-close'),
  );

  $<HTMLButtonElement>('log-copy').addEventListener('click', () => {
    void navigator.clipboard.writeText(log.asText()).then(
      () => toast('Log copied.'),
      () => toast('Could not copy — your browser blocked clipboard access.'),
    );
  });

  for (const btn of document.querySelectorAll<HTMLButtonElement>('[data-modal]')) {
    btn.addEventListener('click', () => openModal(btn.dataset['modal']!));
  }

  $<HTMLButtonElement>('theme-toggle').addEventListener('click', () => {
    applyTheme(document.documentElement.dataset['theme'] === 'light' ? 'dark' : 'light');
  });

  // ── input ───────────────────────────────────────────────────────────────
  const input = $<HTMLTextAreaElement>('paste-input');
  let debounce: ReturnType<typeof setTimeout> | null = null;
  input.addEventListener('input', () => {
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(() => analyse(input.value), 400);
  });

  $<HTMLButtonElement>('try-sample').addEventListener('click', (e) => {
    e.stopPropagation();
    void loadSample('/samples/toolbelt.json', 'the MCP sample');
  });
  $<HTMLButtonElement>('pick-file').addEventListener('click', (e) => {
    e.stopPropagation();
    $<HTMLInputElement>('file-input').click();
  });
  $<HTMLInputElement>('file-input').addEventListener('change', (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) readFile(file);
  });
  $<HTMLButtonElement>('start-over').addEventListener('click', () => {
    input.value = '';
    analysis = null;
    artefacts = [];
    for (const id of ['repair-panel', 'matrix-panel', 'findings-panel', 'gbnf-panel', 'export-panel']) {
      $(id).setAttribute('hidden', '');
    }
    $('start-over').setAttribute('hidden', '');
    $('input-note').textContent = '';
    clearError();
  });

  const dz = $('dropzone');
  for (const type of ['dragenter', 'dragover'] as const) {
    dz.addEventListener(type, (e) => {
      e.preventDefault();
      dz.classList.add('over');
    });
  }
  for (const type of ['dragleave', 'drop'] as const) {
    dz.addEventListener(type, () => dz.classList.remove('over'));
  }
  dz.addEventListener('drop', (e) => {
    e.preventDefault();
    const file = (e as DragEvent).dataTransfer?.files?.[0];
    if (file) readFile(file);
  });

  // Paste from anywhere on the page, as long as the user is not typing in the box already.
  document.addEventListener('paste', (e) => {
    if (document.activeElement === input) return;
    const text = (e as ClipboardEvent).clipboardData?.getData('text');
    if (text && text.trim().length > 1) {
      e.preventDefault();
      ingestText(text, 'the clipboard');
    }
  });

  $<HTMLSelectElement>('filter-severity').addEventListener('change', () => {
    if (analysis) renderFindings(analysis);
  });
  $<HTMLSelectElement>('filter-target').addEventListener('change', () => {
    if (analysis) renderFindings(analysis);
  });

  $<HTMLButtonElement>('dl-all').addEventListener('click', () => {
    if (artefacts.length === 0) return;
    download({
      filename: 'toolwright-output.txt',
      mime: 'text/plain',
      content: bundle(artefacts),
      note: '',
    });
  });

  $<HTMLButtonElement>('share').addEventListener('click', () => {
    if (artefacts.length === 0) return;
    const files = artefacts.map((a) => new File([a.content], a.filename, { type: a.mime }));
    if (navigator.canShare?.({ files })) {
      void navigator.share({ files, title: 'Toolwright output' }).catch(() => undefined);
    } else {
      void navigator.share?.({ title: 'Toolwright', url: location.href }).catch(() => undefined);
    }
  });

  $<HTMLButtonElement>('retry').addEventListener('click', () => {
    clearError();
    analyse(input.value);
  });

  // ── keyboard ────────────────────────────────────────────────────────────
  document.addEventListener('keydown', (e) => {
    if (e.key === '?' && !isTyping(e)) {
      e.preventDefault();
      openModal('keys');
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      analyse(input.value);
      return;
    }
    if (isTyping(e)) return;
    if (e.key === 'e' || e.key === 'E') $('export-panel').scrollIntoView({ behavior: 'smooth' });
    if (e.key === 'l' || e.key === 'L') drawer.open();
  });

  log.emit('good', 'Ready. Nothing you paste will leave this tab.');
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'TEXTAREA' || t.tagName === 'INPUT' || t.isContentEditable);
}

boot();
